import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: 'general',
    message: '',
    preferredContact: 'email'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const inquiryTypes = [
    { value: 'general', label: 'General Questions', icon: 'MessageCircle' },
    { value: 'catering', label: 'Catering Services', icon: 'Users' },
    { value: 'events', label: 'Private Events', icon: 'Calendar' },
    { value: 'feedback', label: 'Feedback', icon: 'Heart' },
    { value: 'dietary', label: 'Dietary Accommodations', icon: 'Leaf' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form after success message
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          inquiryType: 'general',
          message: '',
          preferredContact: 'email'
        });
      }, 3000);
    }, 1500);
  };

  if (isSubmitted) {
    return (
      <div className="bg-success-50 border border-success-200 rounded-xl p-8 text-center">
        <div className="bg-success-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} className="text-success-600" />
        </div>
        <h3 className="font-headline text-2xl font-bold text-success-800 mb-2">
          Message Sent Successfully!
        </h3>
        <p className="text-success-700 mb-4">
          Thank you for reaching out! We'll get back to you within 2 hours during business hours.
        </p>
        <div className="flex items-center justify-center space-x-2 text-success-600">
          <Icon name="Clock" size={16} />
          <span className="text-sm font-medium">Expected response: 2 hours</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-xl shadow-warm p-8">
      <div className="mb-6">
        <h2 className="font-headline text-2xl font-bold text-text-primary mb-2">
          Send Us a Message
        </h2>
        <p className="text-text-secondary">
          We'd love to hear from you. Fill out the form below and we'll respond quickly.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name and Email Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-text-primary mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-organic"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-organic"
              placeholder="your.email@example.com"
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-organic"
            placeholder="(555) 123-4567"
          />
        </div>

        {/* Inquiry Type */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-3">
            What can we help you with? *
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {inquiryTypes.map((type) => (
              <label
                key={type.value}
                className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-organic ${
                  formData.inquiryType === type.value
                    ? 'border-primary-500 bg-primary-50 text-primary-700' :'border-primary-200 hover:border-primary-300 hover:bg-primary-50'
                }`}
              >
                <input
                  type="radio"
                  name="inquiryType"
                  value={type.value}
                  checked={formData.inquiryType === type.value}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <Icon name={type.icon} size={20} />
                <span className="text-sm font-medium">{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-text-primary mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            required
            rows={5}
            className="w-full px-4 py-3 border border-primary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-organic resize-none"
            placeholder="Tell us more about your inquiry..."
          />
        </div>

        {/* Preferred Contact Method */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-3">
            Preferred Contact Method
          </label>
          <div className="flex space-x-6">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="preferredContact"
                value="email"
                checked={formData.preferredContact === 'email'}
                onChange={handleInputChange}
                className="text-primary focus:ring-primary-500"
              />
              <Icon name="Mail" size={16} className="text-text-secondary" />
              <span className="text-sm font-medium text-text-primary">Email</span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="radio"
                name="preferredContact"
                value="phone"
                checked={formData.preferredContact === 'phone'}
                onChange={handleInputChange}
                className="text-primary focus:ring-primary-500"
              />
              <Icon name="Phone" size={16} className="text-text-secondary" />
              <span className="text-sm font-medium text-text-primary">Phone</span>
            </label>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Sending Message...</span>
            </>
          ) : (
            <>
              <Icon name="Send" size={20} />
              <span>Send Message</span>
            </>
          )}
        </button>

        {/* Privacy Note */}
        <p className="text-xs text-text-secondary text-center">
          By submitting this form, you agree to our privacy policy. We'll never share your information with third parties.
        </p>
      </form>
    </div>
  );
};

export default ContactForm;